import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './ShiftTimePicker.scss';

const parseDisplayTime = (value = '') => {
  const match = `${value}`.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return { hour: '6', minute: '30', period: 'AM' };
  return {
    hour: match[1],
    minute: match[2],
    period: match[3].toUpperCase(),
  };
};

const normalizeHour = (value) => {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return '';
  return `${Math.min(Math.max(numberValue, 1), 12)}`;
};

const normalizeMinute = (value) => {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return '';
  return `${Math.min(Math.max(numberValue, 0), 59)}`.padStart(2, '0');
};

const getDigitValue = (value, maxLength) => `${value}`.replace(/\D/g, '').slice(0, maxLength);

const isAllowedInputKey = (event) => (
  /^\d$/.test(event.key)
  || ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)
  || ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase()))
);

const blockInvalidKeys = (event) => {
  if (!isAllowedInputKey(event)) event.preventDefault();
};

const ShiftTimePicker = ({ value, placeholder, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [{ hour, minute, period }, setDraft] = useState(parseDisplayTime(value));
  const pickerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) setDraft(parseDisplayTime(value));
  }, [isOpen, value]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen]);

  const handleSelect = () => {
    const nextHour = normalizeHour(hour) || '12';
    const nextMinute = normalizeMinute(minute) || '00';
    onChange(`${nextHour}:${nextMinute} ${period}`);
    setIsOpen(false);
  };

  return (
    <div ref={pickerRef} className="shift-time-picker">
      <button
        type="button"
        className={`shift-time-picker__trigger${isOpen ? ' active' : ''}`}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span>{value || placeholder}</span>
        <i className="pi pi-chevron-down" />
      </button>
      {isOpen && (
        <div className="shift-time-picker__panel">
          <div className="shift-time-picker__row">
            <input
              aria-label="Hour"
              className="shift-time-picker__input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              min="1"
              max="12"
              value={hour}
              onChange={(event) => setDraft((prev) => ({
                ...prev,
                hour: getDigitValue(event.target.value, 2),
              }))}
              onKeyDown={blockInvalidKeys}
              onBlur={() => setDraft((prev) => ({ ...prev, hour: normalizeHour(prev.hour) || '12' }))}
            />
            <span className="shift-time-picker__colon">:</span>
            <input
              aria-label="Minute"
              className="shift-time-picker__input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              min="0"
              max="59"
              value={minute}
              onChange={(event) => setDraft((prev) => ({
                ...prev,
                minute: getDigitValue(event.target.value, 2),
              }))}
              onKeyDown={blockInvalidKeys}
              onBlur={() => setDraft((prev) => ({ ...prev, minute: normalizeMinute(prev.minute) || '00' }))}
            />
            <div className="shift-time-picker__period">
              {['AM', 'PM'].map((item) => (
                <button
                  key={item}
                  type="button"
                  className={period === item ? 'active' : ''}
                  onClick={() => setDraft((prev) => ({ ...prev, period: item }))}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
          <button type="button" className="shift-time-picker__select" onClick={handleSelect}>
            Select
          </button>
        </div>
      )}
    </div>
  );
};

ShiftTimePicker.propTypes = {
  value: PropTypes.string,
  placeholder: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

ShiftTimePicker.defaultProps = {
  value: '',
};

export default ShiftTimePicker;
